import { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import api from '../services/api';
import { safeDecode } from '../utils/decode';
import { numeric } from '../quotas/QuotaTable';
import { fieldErrors, wranglerError } from './errors';

const plural = (n, word) => `${n.toLocaleString()} ${word}${n !== 1 ? 's' : ''}`;

export const BULK_ACTIONS = {
  on: { label: 'Switch ON', done: (n) => `Switched ON ${plural(n, 'quota')}.` },
  off: { label: 'Switch OFF', done: (n) => `Switched OFF ${plural(n, 'quota')}.` },
  reviewed: { label: 'Mark reviewed', done: (n) => `Marked ${plural(n, 'quota')} as reviewed.` },
  increase: { label: 'Increase…', done: (n) => `Increased ${plural(n, 'quota')}.` },
  'set-limit': { label: 'Set limit…', done: (n) => `Set the limit of ${plural(n, 'quota')}.` },
  delete: { label: 'Delete', done: (n) => `Deleted ${plural(n, 'quota')}.` },
};

const UPDATES = { on: { is_on: true }, off: { is_on: false }, reviewed: { needs_review: false } };

/** A Wrangler reply we can't be sure about: the change may or may not have been made. */
const uncertain = (err) => !err?.response || err.response.status >= 500;

const NameList = ({ quotas }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, my: 1.5 }}>
    <Table size="small" stickyHeader>
      <TableHead><TableRow><TableCell>Quota</TableCell><TableCell sx={numeric}>Completes / Limit</TableCell></TableRow></TableHead>
      <TableBody>
        {quotas.map((q) => (
          <TableRow key={q.id}>
            <TableCell>{safeDecode(q.name)}</TableCell>
            <TableCell sx={numeric}>{q.complete_count} / {q.current_limit}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/** Old and new limits, as Wrangler worked them out (a dry run) or applied them. */
const ChangeList = ({ changed, unchanged = [], byId }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, my: 1.5 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>Quota</TableCell>
          <TableCell sx={numeric}>Completes</TableCell>
          <TableCell sx={numeric}>Limit</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {changed.map((c) => {
          const closes = c.new_limit <= (byId.get(c.quota_id)?.complete_count ?? -1) && c.new_limit < c.previous_limit;
          return (
            <TableRow key={c.quota_id}>
              <TableCell>{safeDecode(c.name)}</TableCell>
              <TableCell sx={numeric}>{byId.get(c.quota_id)?.complete_count ?? '—'}</TableCell>
              <TableCell sx={{ ...numeric, color: closes ? 'warning.dark' : undefined }}>
                {c.previous_limit} → <strong>{c.new_limit}</strong> ({c.new_limit >= c.previous_limit ? '+' : '−'}{Math.abs(c.new_limit - c.previous_limit)})
              </TableCell>
            </TableRow>
          );
        })}
        {unchanged.map((u) => (
          <TableRow key={u.quota_id}>
            <TableCell sx={{ color: 'text.secondary' }}>{safeDecode(u.name)}</TableCell>
            <TableCell sx={numeric}>{byId.get(u.quota_id)?.complete_count ?? '—'}</TableCell>
            <TableCell sx={{ ...numeric, color: 'text.secondary' }}>{u.limit} (already)</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const sameChanges = (a, b) => JSON.stringify((a || []).map((c) => [c.quota_id, c.new_limit]))
  === JSON.stringify((b || []).map((c) => [c.quota_id, c.new_limit]));

/**
 * The confirmation for a bulk action on the ticked quotas (design §2). Increases and limit
 * changes show Wrangler's own dry run first, then the result of the real run. Nothing is retried:
 * if Ranger can't tell whether Wrangler applied a change, it says so and asks for a reload.
 */
export const BulkDialog = ({ surveyId, kind, quotas, onClose, onApplied }) => {
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');
  const [step, setStep] = useState(kind === 'increase' || kind === 'set-limit' ? 'form' : 'check');
  const [check, setCheck] = useState(null);
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const ids = quotas.map((q) => q.id);
  const byId = new Map(quotas.map((q) => [q.id, q]));
  const action = BULK_ACTIONS[kind];

  const number = Number(value);
  const valid = kind === 'increase'
    ? value !== '' && Number.isFinite(number) && number > 0 && (mode === 'percent' || Number.isInteger(number))
    : value !== '' && Number.isInteger(number) && number >= 0;
  const limitBody = () => (kind === 'increase'
    ? { quota_ids: ids, ...(mode === 'amount' ? { amount: number } : { percent: String(value) }) }
    : { quota_ids: ids, limit: number });

  const joinUnknown = kind === 'on' ? quotas.filter((q) => q.group_join_unknown) : [];
  const copies = kind === 'on' ? quotas.filter((q) => q.alchemer_quota_id != null && !q.is_on) : [];

  const send = async (request, after, { real = true } = {}) => {
    setPending(true);
    setError(null);
    try {
      const { data } = await request();
      after(data);
    } catch (err) {
      if (real && uncertain(err)) {
        setStep('uncertain');
      } else {
        setError([wranglerError(err), ...fieldErrors(err)].join('\n'));
      }
    } finally {
      setPending(false);
    }
  };

  const dryRun = () => send(
    () => api.post(`/surveys/${surveyId}/wrangler/quotas/bulk-${kind}/`, { ...limitBody(), dry_run: true }),
    (data) => { setCheck(data); setStep('check'); },
    { real: false },
  );

  const apply = () => {
    if (kind === 'increase' || kind === 'set-limit') {
      send(() => api.post(`/surveys/${surveyId}/wrangler/quotas/bulk-${kind}/`, limitBody()), (data) => {
        setResult(data);
        setStep('result');
      });
    } else if (kind === 'delete') {
      send(() => api.post(`/surveys/${surveyId}/wrangler/quotas/bulk-delete/`, { quota_ids: ids }),
        (data) => onApplied(kind, data));
    } else {
      send(() => api.post(`/surveys/${surveyId}/wrangler/quotas/bulk-update/`, { quota_ids: ids, ...UPDATES[kind] }),
        (data) => onApplied(kind, data));
    }
  };

  const title = {
    on: `Switch ON ${plural(quotas.length, 'quota')}?`,
    off: `Switch OFF ${plural(quotas.length, 'quota')}?`,
    reviewed: `Mark ${plural(quotas.length, 'quota')} as reviewed?`,
    delete: `Delete ${plural(quotas.length, 'quota')}?`,
    increase: `Increase ${plural(quotas.length, 'quota')}`,
    'set-limit': `Set the limit of ${plural(quotas.length, 'quota')}`,
  }[kind];

  const closing = check?.changed?.filter((c) => c.new_limit <= (byId.get(c.quota_id)?.complete_count ?? -1)) || [];

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {step === 'form' && kind === 'increase' && (
          <>
            <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setMode(v)} sx={{ mb: 2 }}>
              <ToggleButton value="percent" sx={{ textTransform: 'none' }}>By a percentage</ToggleButton>
              <ToggleButton value="amount" sx={{ textTransform: 'none' }}>By a number</ToggleButton>
            </ToggleButtonGroup>
            <TextField autoFocus fullWidth size="small" type="number" label={mode === 'amount' ? 'Add' : 'Increase by'}
              value={value} onChange={(e) => setValue(e.target.value)}
              InputProps={mode === 'percent' ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : undefined} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {mode === 'percent'
                ? "Each quota's own current limit, rounded to the nearest whole number (halves up), at least 1."
                : 'The same number is added to every ticked quota.'}
            </Typography>
          </>
        )}
        {step === 'form' && kind === 'set-limit' && (
          <TextField autoFocus fullWidth size="small" type="number" label="New limit for every ticked quota" value={value}
            onChange={(e) => setValue(e.target.value)} sx={{ mt: 1 }} />
        )}

        {step === 'check' && check && (
          <>
            <Typography variant="body2">
              As Wrangler works it out now. Nothing has changed yet.
            </Typography>
            <ChangeList changed={check.changed} unchanged={check.unchanged} byId={byId} />
            {closing.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {plural(closing.length, 'quota')} will be at or over the new limit, so {closing.length === 1 ? 'it closes' : 'they close'} at once.
              </Alert>
            )}
          </>
        )}
        {step === 'check' && !check && (
          <>
            <NameList quotas={quotas} />
            {kind === 'on' && joinUnknown.length > 0 && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {plural(joinUnknown.length, 'quota')} can&apos;t be switched ON until AND or OR is chosen
                ({joinUnknown.map((q) => safeDecode(q.name)).join(', ')}). Untick {joinUnknown.length === 1 ? 'it' : 'them'}, or
                open {joinUnknown.length === 1 ? 'it' : 'each'} to choose. Wrangler switches all or none.
              </Alert>
            )}
            {kind === 'on' && copies.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {plural(copies.length, 'quota')} {copies.length === 1 ? 'is a copy' : 'are copies'} of Alchemer quotas whose originals
                are still active, so both will block respondents. Copies may close slightly earlier, because they also count
                people holding a place.
              </Alert>
            )}
            {kind === 'off' && <Typography variant="body2">They keep counting, but block nobody.</Typography>}
            {kind === 'delete' && (
              <Alert severity="warning">
                Their limit history goes with them, quota views and trackers that include them lose them, and exemption rules
                that name them stop covering them. Respondents they would have blocked are let through. This can&apos;t be undone.
              </Alert>
            )}
          </>
        )}

        {step === 'result' && result && (
          <>
            <Alert severity="success" sx={{ mb: 1 }}>{action.done(result.changed.length)}</Alert>
            {!sameChanges(result.changed, check?.changed) && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Some limits changed between the check and now, so these are the numbers Wrangler actually applied.
              </Alert>
            )}
            <ChangeList changed={result.changed} unchanged={result.unchanged} byId={byId} />
          </>
        )}

        {step === 'uncertain' && (
          <Alert severity="warning">
            Ranger couldn&apos;t tell whether Wrangler made the change. It isn&apos;t sent again automatically, so nothing is
            applied twice: close this to reload the list and see what happened before trying again.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
            {step === 'form' ? '' : 'Nothing was changed. '}{error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'result' || step === 'uncertain' ? (
          <Button variant="contained" onClick={() => onApplied(kind, result, { reload: true })} sx={{ textTransform: 'none' }}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={pending} sx={{ textTransform: 'none' }}>Cancel</Button>
            {step === 'check' && check && (
              <Button onClick={() => setStep('form')} disabled={pending} sx={{ textTransform: 'none' }}>Back</Button>
            )}
            {step === 'form' ? (
              <Button variant="contained" onClick={dryRun} disabled={!valid || pending} sx={{ textTransform: 'none' }}>
                {pending ? 'Checking…' : 'Check'}
              </Button>
            ) : (
              <Button variant="contained" color={kind === 'delete' ? 'error' : 'primary'} onClick={apply}
                disabled={pending || joinUnknown.length > 0 || (check && check.changed.length === 0)}
                sx={{ textTransform: 'none' }}>
                {pending ? 'Working…' : {
                  on: 'Switch ON', off: 'Switch OFF', reviewed: 'Mark reviewed', delete: 'Delete',
                  increase: `Increase ${plural(check?.changed.length || 0, 'quota')}`,
                  'set-limit': `Change ${plural(check?.changed.length || 0, 'quota')}`,
                }[kind]}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

/** The bar of actions shown while quotas are ticked. */
export const BulkBar = ({ count, hidden, onAction, onClear }) => (
  <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2, py: 1, mb: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
    <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
      {plural(count, 'quota')} ticked{hidden > 0 && `, ${hidden.toLocaleString()} hidden by the filter`}
    </Typography>
    {Object.entries(BULK_ACTIONS).map(([kind, a]) => (
      <Button key={kind} size="small" variant="outlined" color={kind === 'delete' ? 'error' : 'primary'}
        onClick={() => onAction(kind)} sx={{ textTransform: 'none', bgcolor: 'background.paper' }}>
        {a.label}
      </Button>
    ))}
    <Box sx={{ flex: 1 }} />
    <Button size="small" onClick={onClear} sx={{ textTransform: 'none' }}>Clear</Button>
  </Paper>
);
